!function () {
  "use strict";

  const ROWS_HOLDER = "[data-timeline-rows]";
  const ROW = "[data-timeline-row]";
  const ROW_EDITOR = "[data-timeline-row-editor]";
  const BUTTON = "[data-timeline-button]";
  const BLOCK_SCROLL = "[data-block-scroll]";
  const SELECTED = "is-selected";
  const DIMMED = "is-dimmed";

  customElements.get("timeline-component") ||
    customElements.define(
      "timeline-component",
      class extends HTMLElement {
        constructor() {
          super();
          this.rows = this.querySelectorAll(ROW);
          if (this.rows.length < 2) return;

          this.rowsWidth = 0;
          this.rowsHeight = [];
          this.holderTop = this.getBoundingClientRect().top + window.scrollY;
          this.holderHeight = this.offsetHeight;
          this.buttons = this.querySelectorAll(BUTTON);
          this.rowsHolder = this.querySelector(ROWS_HOLDER);
          this.requestAnimation = null;
          this.isDesktopView = !window.theme.isMobile();
          this.isScrollEnabled = false;
          this.scrollEvent = (e) => this.scrollEvents(e);
          this.resizeEvent = () => this.resizeEvents();
          this.observer = null;
          this.activeRow = null;
        }

        connectedCallback() {
          this.calculateRowsDimensions();
          this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

          if (window.theme.isMobile()) {
            this.setupMobileObserver();
          } else {
            if (!this.isScrollEnabled) {
              this.isScrollEnabled = true;
              document.addEventListener("theme:scroll", this.scrollEvent);
            }
            this.rowsHolder.addEventListener("scroll", this.scrollEvent);
          }

          document.addEventListener("theme:resize:width", this.resizeEvent);

          this.buttons.length &&
            this.buttons.forEach((btn, index) => {
              btn.addEventListener("click", (e) => {
                e.preventDefault();
                const row = e.currentTarget.closest(ROW);
                if (!row) return;

                if (window.theme.isMobile()) {
                  const offset = row.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top: offset, behavior: "smooth" });
                } else {
                  const maxHeight = Math.max(...this.rowsHeight);
                  const segmentHeight = this.holderHeight / this.rows.length;
                  const holderTop = this.getBoundingClientRect().top;
                  const scrollTarget =
                    (index > 0 && maxHeight < segmentHeight
                      ? segmentHeight * index + holderTop
                      : holderTop) + 1;
                  window.theme.scrollTo(scrollTarget);
                }
              });
            });
        }

        setupMobileObserver() {
          if (this.observer) this.observer.disconnect();

          // Mark first row as active by default
          if (this.rows.length) {
            this.activeRow = this.rows[0];
            this.rows[0].classList.add(SELECTED);
            this.rows[0].classList.remove(DIMMED);
            this.rows.forEach((row, i) => {
              if (i !== 0) row.classList.add(DIMMED);
            });
          }

          // Large rootMargin so the row is "active" well before it fully enters view
          // threshold 0 means trigger as soon as any part is visible
          const options = {
            root: null,
            rootMargin: "-10% 0px -50% 0px",
            threshold: 0,
          };

          let ticking = false;

          this.observer = new IntersectionObserver((entries) => {
            if (ticking) return;
            ticking = true;

            // Small debounce so rapid scroll doesn't cause flicker
            setTimeout(() => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  const incoming = entry.target;

                  // Only update if it's actually a different row
                  if (this.activeRow === incoming) {
                    ticking = false;
                    return;
                  }

                  this.activeRow = incoming;

                  this.rows.forEach((row) => {
                    if (row === incoming) {
                      row.classList.add(SELECTED);
                      row.classList.remove(DIMMED);
                    } else {
                      row.classList.remove(SELECTED);
                      row.classList.add(DIMMED);
                    }
                  });
                }
              });
              ticking = false;
            }, 80); // 80ms debounce — smooth but responsive
          }, options);

          this.rows.forEach((row) => this.observer.observe(row));
        }

        resizeEvents() {
          this.holderTop = this.getBoundingClientRect().top + window.scrollY;
          this.holderHeight = this.offsetHeight;
          this.calculateRowsDimensions();
          this.requestAnimation = requestAnimationFrame(() => this.calculatePosition());

          const isNowDesktop = !window.theme.isMobile();

          if (isNowDesktop && !this.isScrollEnabled) {
            this.isScrollEnabled = true;
            document.addEventListener("theme:scroll", this.scrollEvent);
            if (this.observer) {
              this.observer.disconnect();
              this.observer = null;
            }
            this.rowsHolder.addEventListener("scroll", this.scrollEvent);
            // Remove mobile classes
            this.rows.forEach((row) => {
              row.classList.remove(DIMMED);
            });
          } else if (!isNowDesktop && this.isScrollEnabled) {
            this.isScrollEnabled = false;
            document.removeEventListener("theme:scroll", this.scrollEvent);
            this.rowsHolder.removeEventListener("scroll", this.scrollEvent);
            this.setupMobileObserver();
          }
        }

        scrollEvents(e) {
          if (!this.requestAnimation) {
            const target = e.currentTarget;
            this.requestAnimation = requestAnimationFrame(() =>
              this.calculatePosition(target)
            );
          }
        }

        removeAnimationFrame() {
          if (this.requestAnimation) {
            cancelAnimationFrame(this.requestAnimation);
            this.requestAnimation = null;
          }
        }

        calculateRowsDimensions() {
          if (!this.rows.length) return;
          this.rowsHeight = [];
          this.rowsWidth = 0;
          let cumulativeHeight = 0;

          this.rows.forEach((row, index) => {
            this.rowsHeight.push(row.offsetHeight);
            this.rowsWidth += row.offsetWidth + parseInt(getComputedStyle(row).marginRight);

            if (window.Shopify.designMode) {
              const editorRows = this.querySelectorAll(ROW_EDITOR);
              if (editorRows.length) {
                cumulativeHeight += row.offsetHeight;
                editorRows[index]?.style.setProperty("--row-height-min", `${row.offsetHeight}px`);
                editorRows[index + 1]?.style.setProperty("--row-top-mobile", `${cumulativeHeight}px`);
              }
            }
          });
        }

        calculatePosition(scrollTarget = null) {
          this.removeAnimationFrame();

          // Skip horizontal scroll logic on mobile
          if (window.theme.isMobile()) return;

          this.holderTop = this.getBoundingClientRect().top + window.scrollY;
          const segmentHeight = this.holderHeight / this.rows.length;
          let currentTop = this.holderTop;
          const viewportHeight = window.innerHeight;
          const scrollThreshold = window.scrollY + 1.5 * viewportHeight;

          if (
            scrollThreshold < this.holderTop ||
            scrollThreshold > this.holderTop + this.holderHeight + viewportHeight
          )
            return;

          this.rows.forEach((row, index) => {
            if (index > 0) {
              const isActive = currentTop + segmentHeight < scrollThreshold;

              if (isActive && row.previousElementSibling) {
                row.previousElementSibling.style.setProperty(
                  "--percent-desktop",
                  ((scrollThreshold - currentTop) / segmentHeight) * 100 + "%"
                );
              }

              row.classList.toggle(SELECTED, isActive);
            }
            currentTop += segmentHeight;
          });
        }

        disconnectedCallback() {
          if (this.isScrollEnabled) {
            this.isScrollEnabled = false;
            document.removeEventListener("theme:scroll", this.scrollEvent);
          }
          this.rowsHolder?.removeEventListener("scroll", this.scrollEvent);
          document.removeEventListener("theme:resize:width", this.resizeEvent);
          if (this.observer) this.observer.disconnect();
        }

        onBlockSelect(e) {
          const holder = this.querySelector(BLOCK_SCROLL);
          if (holder) {
            const block = e.srcElement;
            const index = [...block.parentElement.children].indexOf(block);
            const row = this.rows[index];
            if (row) {
              holder.scrollTo({ top: 0, left: row.offsetLeft, behavior: "smooth" });
            }
          }
        }
      }
    );
}();
